import { AxiosError } from "axios"
import { resolveCurrencyImage } from "@/helpers/currencyImage"
import { buildScaleIndex, formatMoney, hasValue } from "@/helpers/money"
import {
  formatAmount,
  formatItemStatValue,
  formatStatRange,
  formatStatValue,
  IFormattedStat,
  statUnitFor
} from "@/helpers/statFormat"
import {
  ICurrencyEntry,
  IEquipmentSlot,
  IStatEntry
} from "@/store/apis/catalogsAPI/catalogs.types"
import {
  IConsumableInfo,
  IInventoryItem,
  IInventoryItemPrice,
  IInventoryWeight,
  IItemRequirement,
  IQuickSlot,
  IStatValue
} from "@/store/apis/inventoryAPI/inventory.types"

export interface IInventoryTabsView {
  slotsByPosition: Record<string, IInventoryItem | null>
  equipmentList: IInventoryItem[]
  consumables: IInventoryItem[]
  resources: IInventoryItem[]
}

export const deriveInventoryTabsView = (
  items: IInventoryItem[],
  layout: IEquipmentSlot[]
): IInventoryTabsView => {
  const slotsByPosition: Record<string, IInventoryItem | null> = {}
  for (const slot of layout) {
    slotsByPosition[slot.position] = null
  }
  for (const it of items) {
    if (it.slotPosition !== null && it.slotPosition in slotsByPosition) {
      slotsByPosition[it.slotPosition] = it
    }
  }

  const equipmentList = items.filter(
    (it) =>
      it.kind === "EQUIPMENT" &&
      (layout.length === 0 || it.slotPosition === null)
  )

  return {
    slotsByPosition,
    equipmentList,
    consumables: items.filter((it) => it.kind === "CONSUMABLE"),
    resources: items.filter((it) => it.kind === "RESOURCE")
  }
}

export const pickEquipTargetPosition = (
  item: IInventoryItem,
  layout: IEquipmentSlot[],
  wornItems: IInventoryItem[]
): string | null => {
  const allowed = item.allowedSlots
  if (!allowed || allowed.length === 0) {
    return null
  }
  const compatible = layout.filter((slot) => allowed.includes(slot.slotType))
  if (compatible.length === 0) {
    return null
  }
  const occupied = new Set(
    wornItems
      .map((it) => it.slotPosition)
      .filter((p): p is string => p !== null)
  )
  const free = compatible.find((slot) => !occupied.has(slot.position))
  return (free ?? compatible[0]).position
}

export const formatWeightText = (weight: IInventoryWeight): string =>
  `${formatAmount(weight.occupied)} / ${formatAmount(weight.maxCapacity)}`

export interface IItemDetailFlags {
  rarity: boolean
  totalWeight: boolean
  price: boolean
  level: boolean
  stats: boolean
  requirements: boolean
  effects: boolean
  durability: boolean
}

export const itemDetailFlags = (item: IInventoryItem): IItemDetailFlags => ({
  rarity: item.hasRarityDimension,
  totalWeight: item.quantity > 1,
  price: hasValue(item.price.amount),
  level: item.level !== null,
  stats: item.stats.length > 0,
  requirements: item.requirements.length > 0,
  effects:
    item.consumable !== null &&
    (item.consumable.effects.duration.length > 0 ||
      item.consumable.effects.instant.length > 0),
  durability: item.maxDurability !== null
})

export interface IItemPriceView {
  amountText: string
  iconUrl: string | undefined
}

export const itemPriceView = (
  price: IInventoryItemPrice,
  currencies: readonly ICurrencyEntry[],
  locale: string
): IItemPriceView | null => {
  const scale = buildScaleIndex(currencies).get(price.currencyCode)
  if (!hasValue(price.amount) || scale === undefined) {
    return null
  }
  const amountText = formatMoney(price.amount, scale, locale)
  return amountText === null
    ? null
    : { amountText, iconUrl: resolveCurrencyImage(price.currencyCode) }
}

export const hasUnmetRequirements = (item: IInventoryItem): boolean =>
  item.requirements.some((req) => !req.met)

export const isBroken = (item: IInventoryItem): boolean => item.durability === 0

export type IRequirementDescriptor =
  | { type: "level"; value: number }
  | { type: "archetype"; targetKey: string }
  | {
      type: "stat"
      targetKey: string
      minValue: number
      currentValue: number | null
    }
  | { type: "unknown" }

export const describeRequirement = (
  req: IItemRequirement
): IRequirementDescriptor => {
  if (req.kind === "LEVEL" && req.minValue !== null) {
    return { type: "level", value: req.minValue }
  }
  if (req.kind === "ARCHETYPE" && req.targetKey !== null) {
    return { type: "archetype", targetKey: req.targetKey }
  }
  if (req.kind === "STAT" && req.targetKey !== null && req.minValue !== null) {
    return {
      type: "stat",
      targetKey: req.targetKey,
      minValue: req.minValue,
      // The API always carries this key — it is `number | null` on every
      // requirement kind, and archetype rows send an explicit null. The
      // coalesce is defensive only.
      currentValue: req.currentValue ?? null
    }
  }
  return { type: "unknown" }
}

const KNOWN_DENIAL_REASONS = new Set([
  "INSTANCE_NOT_FOUND",
  "WRONG_OWNER",
  "NOT_EQUIPMENT",
  "SLOT_INCOMPATIBLE",
  "VERSION_CONFLICT",
  "CONFLICT_RETRY_EXHAUSTED",
  "NOT_DESTROYABLE",
  "INSTANCE_EQUIPPED",
  "INSUFFICIENT_QUANTITY",
  "LEVEL_TOO_LOW",
  "ARCHETYPE_MISMATCH",
  "REQUIREMENT_UNSUPPORTED",
  "NOT_CONSUMABLE",
  "INVALID_SLOT_INDEX",
  "UNKNOWN_DEFINITION",
  "ITEM_BROKEN",
  "STAT_TOO_LOW"
])

export const denialReasonKey = (reason?: string): string =>
  reason && KNOWN_DENIAL_REASONS.has(reason)
    ? `inventory:denial.${reason}`
    : "inventory:denial.generic"

export const extractDenialReason = (error: unknown): string | undefined =>
  (error as AxiosError<{ reason?: string }> | undefined)?.response?.data?.reason

export interface IDurationPart {
  unit: "h" | "min" | "s"
  value: number
}

export const describeDuration = (totalSeconds: number): IDurationPart[] => {
  const parts: IDurationPart[] = [
    { unit: "h", value: Math.floor(totalSeconds / 3600) },
    { unit: "min", value: Math.floor((totalSeconds % 3600) / 60) },
    { unit: "s", value: totalSeconds % 60 }
  ]
  return parts.filter((part) => part.value > 0).slice(0, 2)
}

export type IEffectRow =
  | { kind: "instant"; vitalCode: string; value: number; formatted: string }
  | {
      kind: "duration"
      statCode: string
      value: number
      formatted: IFormattedStat
    }

export const buildEffectRows = (
  consumable: IConsumableInfo,
  statCatalog: IStatEntry[]
): IEffectRow[] => [
  ...consumable.effects.instant.map(
    (effect): IEffectRow => ({
      kind: "instant",
      vitalCode: effect.vitalCode,
      value: effect.amount,
      formatted: formatStatValue(effect.amount, "INTEGER")
    })
  ),
  ...consumable.effects.duration.map(
    (effect): IEffectRow => ({
      kind: "duration",
      statCode: effect.statCode,
      value: effect.value,
      formatted: formatItemStatValue(
        effect.value,
        statUnitFor(statCatalog, effect.statCode),
        effect.operation
      )
    })
  )
]

const DAMAGE_MIN_CODE = "damage_min"
const DAMAGE_MAX_CODE = "damage_max"
const DAMAGE_RANGE_CODE = "damage"

export type IStatRow =
  | {
      kind: "single"
      code: string
      value: number
      formatted: IFormattedStat
    }
  | {
      kind: "range"
      code: typeof DAMAGE_RANGE_CODE
      min: number
      max: number
      formatted: string
    }

export const buildStatRows = (
  stats: IStatValue[],
  statCatalog: IStatEntry[]
): IStatRow[] => {
  const toSingleRow = (stat: IStatValue): IStatRow => ({
    kind: "single",
    code: stat.code,
    value: stat.value,
    formatted: formatItemStatValue(
      stat.value,
      statUnitFor(statCatalog, stat.code),
      stat.operation
    )
  })
  const min = stats.find((stat) => stat.code === DAMAGE_MIN_CODE)
  const max = stats.find((stat) => stat.code === DAMAGE_MAX_CODE)
  if (!min || !max) {
    return stats.map(toSingleRow)
  }
  return stats.flatMap((stat): IStatRow[] => {
    if (stat.code === DAMAGE_MAX_CODE) {
      return []
    }
    if (stat.code === DAMAGE_MIN_CODE) {
      return [
        {
          kind: "range",
          code: DAMAGE_RANGE_CODE,
          min: min.value,
          max: max.value,
          formatted: formatStatRange(min.value, max.value)
        }
      ]
    }
    return [toSingleRow(stat)]
  })
}

export interface IQuickSlotView {
  definitionCode: string
  firstInstanceId: string
  totalQuantity: number
}

export const deriveQuickSlotsView = (
  quickSlots: (IQuickSlot | null)[],
  items: IInventoryItem[]
): (IQuickSlotView | null)[] =>
  quickSlots.map((slot) => {
    if (!slot) {
      return null
    }
    const stacks = items.filter(
      (stack) => stack.definitionCode === slot.definitionCode
    )
    if (stacks.length === 0) {
      return null
    }
    return {
      definitionCode: slot.definitionCode,
      firstInstanceId: stacks[0].id,
      totalQuantity: stacks.reduce((sum, stack) => sum + stack.quantity, 0)
    }
  })
